import { useEffect, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ENTRELINHAS, fmtNum, type Alinhamento, type FormatoParagrafo } from '../estilos';
import { rotuloDoTimbrado, type Margens, type Timbrado } from '../documento';
import { EditorDeTimbrado, novoTimbrado, type ModoDoTimbrado } from './editorDeTimbrado';
import { BotaoNu } from './comum';

/** Enter aplica; Cancelar fecha sem mudar nada — o contrato dos painéis do editor. */
export function rodapeModal(aoAplicar: () => void, aoFechar: () => void, rotulo: string, desativado = false): ReactNode {
  return (
    <>
      <span className="cm-ef-dica-modal">Enter aplica · Cancelar fecha sem mudar nada</span>
      <div className="cm-ef-acoes">
        <Button type="button" variant="outline" onClick={aoFechar}>Cancelar</Button>
        <Button type="button" disabled={desativado} onClick={aoAplicar}>{rotulo}</Button>
      </div>
    </>
  );
}

/** Painel do editor num Dialog do app: título, descrição, corpo e rodapé de ações. */
export function PainelDialogo({ aberto, aoFechar, titulo, descricao, largo = true, rodape, children }: {
  aberto: boolean; aoFechar: () => void; titulo: string; descricao?: string;
  /** Largo (painéis com prévia ao lado) ou estreito (margens). */
  largo?: boolean;
  rodape: ReactNode; children: ReactNode;
}) {
  return (
    <Dialog open={aberto} onOpenChange={(o) => { if (!o) aoFechar(); }}>
      <DialogContent
        className={`cm-ef-portal cm-ef-dialogo ${largo ? 'max-w-3xl' : 'max-w-md'}`}
        // O Radix focaria o primeiro botão (no Parágrafo, "Esquerda"), e o
        // Enter que o rodapé ensina viraria clique nele em vez de aplicar. O
        // foco vai ao primeiro campo; sem campo, ao próprio painel.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          const painel = e.currentTarget as HTMLElement;
          (painel.querySelector<HTMLElement>('input:not([type="file"]), textarea') ?? painel).focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          {descricao && <DialogDescription>{descricao}</DialogDescription>}
        </DialogHeader>
        {children}
        <DialogFooter className="cm-ef-modal-rod">{rodape}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
export const enterAplica = (aoAplicar: () => void) => (e: KeyboardEvent) => {
  const alvo = e.target as HTMLElement;
  if (e.key === 'Enter' && alvo.tagName !== 'SELECT' && alvo.tagName !== 'BUTTON' && alvo.tagName !== 'TEXTAREA') { e.preventDefault(); aoAplicar(); }
};

export function Numero({ rotulo, valor, unidade, passo, min, max, aoMudar }: { rotulo: string; valor: number; unidade: string; passo: number; min: number; max: number; aoMudar: (v: number) => void }) {
  return (
    <label className="cm-ef-rot">
      {rotulo}
      <span className="cm-ef-campo-un">
        <input className="cm-ef-campo" type="number" step={passo} min={min} max={max} value={valor}
          onChange={(e) => aoMudar(Math.max(min, Math.min(max, Number(e.target.value) || 0)))} />
        <span>{unidade}</span>
      </span>
    </label>
  );
}

/* ===================== Parágrafo ===================== */

const ALINHAMENTOS: { v: Alinhamento; nome: string; d: string }[] = [
  { v: 'left', nome: 'Esquerda', d: 'M4 6h16M4 10h10M4 14h16M4 18h10' },
  { v: 'center', nome: 'Centro', d: 'M4 6h16M7 10h10M4 14h16M7 18h10' },
  { v: 'right', nome: 'Direita', d: 'M4 6h16M10 10h10M4 14h16M10 18h10' },
  { v: 'justify', nome: 'Justificado', d: 'M4 6h16M4 10h16M4 14h16M4 18h16' },
];

export function PainelParagrafo({ aberto, formato, nomeEstilo, aoFechar, aoAplicar }: {
  aberto: boolean; formato: FormatoParagrafo; nomeEstilo: string; aoFechar: () => void; aoAplicar: (f: FormatoParagrafo) => void;
}) {
  const [f, setF] = useState(formato);
  // Só ao abrir: depender de `formato` (objeto novo a cada render) apagaria o que se digita.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (aberto) setF(formato); }, [aberto]);
  const m = (patch: Partial<FormatoParagrafo>) => setF((x) => ({ ...x, ...patch }));
  const aplicar = () => aoAplicar(f);
  const entreLista = ENTRELINHAS.some((e) => e.valor === f.entrelinha);

  // O desenho: quatro linhas do parágrafo entre dois vizinhos, nas proporções.
  const u = 7;
  const lh = 4 + (f.entrelinha - 1) * 8 + 3;
  const linhas = [0, 1, 2, 3].map((i) => {
    const esq = f.recuoEsquerdo * u + (i === 0 ? (f.especial === 'primeira' ? f.medida * u : 0) : f.especial === 'deslocado' ? f.medida * u : 0);
    return <div key={i} className="cm-ef-mini-lin is-foco" style={{ margin: `0 ${f.recuoDireito * u + (i === 3 ? 30 : 0)}px ${i < 3 ? lh - 4 : 0}px ${esq}px` }} />;
  });
  const especial = f.especial === 'primeira' ? `primeira linha a ${fmtNum(f.medida)} cm` : f.especial === 'deslocado' ? `deslocado em ${fmtNum(f.medida)} cm` : 'sem recuo especial';

  return (
    <PainelDialogo
      aberto={aberto}
      aoFechar={aoFechar}
      titulo="Parágrafo"
      descricao={`Vale para o parágrafo do cursor (${nomeEstilo}).`}
      rodape={rodapeModal(aplicar, aoFechar, 'Aplicar ao parágrafo')}
    >
      <div className="cm-ef-painel" onKeyDown={enterAplica(aplicar)}>
        <div className="cm-ef-painel-campos">
          <section className="cm-ef-secao">
            <h3>Alinhamento</h3>
            <div className="cm-ef-seg" role="group" aria-label="Alinhamento">
              {ALINHAMENTOS.map((a) => (
                <BotaoNu key={a.v} aria-label={a.nome} aria-pressed={f.alinhamento === a.v} className="cm-ef-bt" onClick={() => m({ alinhamento: a.v })}>
                  <svg viewBox="0 0 24 24" className="cm-ef-ico" aria-hidden><path d={a.d} /></svg>
                </BotaoNu>
              ))}
            </div>
          </section>
          <section className="cm-ef-secao">
            <h3>Recuos</h3>
            <div className="cm-ef-linhas">
              <Numero rotulo="Esquerda" valor={f.recuoEsquerdo} unidade="cm" passo={0.25} min={0} max={10} aoMudar={(v) => m({ recuoEsquerdo: v })} />
              <Numero rotulo="Direita" valor={f.recuoDireito} unidade="cm" passo={0.25} min={0} max={10} aoMudar={(v) => m({ recuoDireito: v })} />
              <label className="cm-ef-rot">Especial
                <select className="cm-ef-campo" value={f.especial} onChange={(e) => m({ especial: e.target.value as FormatoParagrafo['especial'], medida: e.target.value === 'nenhum' ? 0 : f.medida || 1.25 })}>
                  <option value="nenhum">Nenhum</option>
                  <option value="primeira">Primeira linha</option>
                  <option value="deslocado">Deslocado</option>
                </select>
              </label>
              <Numero rotulo="Medida" valor={f.medida} unidade="cm" passo={0.25} min={0} max={10} aoMudar={(v) => m({ medida: v })} />
            </div>
          </section>
          <section className="cm-ef-secao">
            <h3>Espaçamento</h3>
            <div className="cm-ef-linhas">
              <Numero rotulo="Antes" valor={f.espacoAntes} unidade="pt" passo={3} min={0} max={72} aoMudar={(v) => m({ espacoAntes: v })} />
              <Numero rotulo="Depois" valor={f.espacoDepois} unidade="pt" passo={3} min={0} max={72} aoMudar={(v) => m({ espacoDepois: v })} />
              <label className="cm-ef-rot">Entrelinha
                <select className="cm-ef-campo" value={entreLista ? String(f.entrelinha) : 'mult'} onChange={(e) => { if (e.target.value !== 'mult') m({ entrelinha: Number(e.target.value) }); }}>
                  {ENTRELINHAS.map((e) => <option key={e.valor} value={e.valor}>{e.rotulo}</option>)}
                  <option value="mult">Múltiplos</option>
                </select>
              </label>
              <Numero rotulo="Em" valor={f.entrelinha} unidade="×" passo={0.05} min={0.8} max={3} aoMudar={(v) => m({ entrelinha: v })} />
            </div>
          </section>
        </div>
        <aside className="cm-ef-diagrama" aria-label="Como fica">
          <h3>Como fica</h3>
          <div className="cm-ef-mini">
            <div className="cm-ef-mini-bloco"><div className="cm-ef-mini-lin" style={{ marginBottom: 3 }} /><div className="cm-ef-mini-lin" style={{ marginRight: 40 }} /></div>
            <div style={{ height: Math.max(2, f.espacoAntes * 0.45) }} />
            <div className="cm-ef-mini-bloco">{linhas}</div>
            <div style={{ height: Math.max(2, f.espacoDepois * 0.45) }} />
            <div className="cm-ef-mini-bloco"><div className="cm-ef-mini-lin" style={{ marginBottom: 3 }} /><div className="cm-ef-mini-lin" style={{ marginRight: 40 }} /></div>
          </div>
          <p className="cm-ef-mini-leg">
            Recuo de {fmtNum(f.recuoEsquerdo)} cm à esquerda e {fmtNum(f.recuoDireito)} cm à direita, {especial}. {fmtNum(f.espacoAntes)} pt antes, {fmtNum(f.espacoDepois)} pt depois, entrelinha {fmtNum(f.entrelinha)}.
          </p>
        </aside>
      </div>
    </PainelDialogo>
  );
}

/* ===================== Margens ===================== */

export function PainelMargens({ aberto, margens, aoFechar, aoAplicar }: { aberto: boolean; margens: Margens; aoFechar: () => void; aoAplicar: (m: Margens) => void }) {
  const [m, setM] = useState(margens);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (aberto) setM(margens); }, [aberto]);
  const aplicar = () => aoAplicar(m);
  return (
    <PainelDialogo aberto={aberto} aoFechar={aoFechar} titulo="Margens" descricao="Para todas as páginas da peça." largo={false} rodape={rodapeModal(aplicar, aoFechar, 'Aplicar margens')}>
      <div className="cm-ef-linhas" onKeyDown={enterAplica(aplicar)}>
        <Numero rotulo="Superior" valor={m.superior} unidade="cm" passo={0.25} min={0.5} max={6} aoMudar={(v) => setM({ ...m, superior: v })} />
        <Numero rotulo="Inferior" valor={m.inferior} unidade="cm" passo={0.25} min={0.5} max={6} aoMudar={(v) => setM({ ...m, inferior: v })} />
        <Numero rotulo="Esquerda" valor={m.esquerda} unidade="cm" passo={0.25} min={0.5} max={6} aoMudar={(v) => setM({ ...m, esquerda: v })} />
        <Numero rotulo="Direita" valor={m.direita} unidade="cm" passo={0.25} min={0.5} max={6} aoMudar={(v) => setM({ ...m, direita: v })} />
      </div>
    </PainelDialogo>
  );
}

/* ===================== Papel timbrado ===================== */

const NOVO = '__novo__';
const SEM = '__sem__';

/**
 * O timbrado da peça: escolher um (ou nenhum) e editá-lo. O editor do timbrado
 * em si é o `EditorDeTimbrado`, o mesmo de Configurações → Papel timbrado.
 */
export function PainelTimbrado({ aberto, timbrados, emUso, modos, aoFechar, aoAplicar }: {
  aberto: boolean;
  timbrados: Timbrado[];
  emUso: string | null;
  /** Os modos que o app guarda (sem app, todos). */
  modos?: ModoDoTimbrado[];
  aoFechar: () => void;
  /** `timbrado` vem preenchido quando é cadastro novo ou edição: o app o guarda. */
  aoAplicar: (id: string | null, timbrado?: Timbrado) => void;
}) {
  const novo = () => novoTimbrado(modos?.[0]);
  const [escolha, setEscolha] = useState<string>(emUso ?? SEM);
  const [t, setT] = useState<Timbrado>(() => timbrados.find((x) => x.id === emUso) ?? novo());
  // Ao abrir (ou se a lista mudar com o painel aberto): a escolha e o formulário recomeçam.
  const [visto, setVisto] = useState({ aberto, emUso, timbrados });
  if (visto.aberto !== aberto || visto.emUso !== emUso || visto.timbrados !== timbrados) {
    setVisto({ aberto, emUso, timbrados });
    if (aberto) {
      setEscolha(emUso ?? (timbrados.length ? timbrados[0].id : NOVO));
      setT(timbrados.find((x) => x.id === emUso) ?? timbrados[0] ?? novo());
    }
  }

  const mudarEscolha = (v: string) => {
    setEscolha(v);
    if (v === NOVO) setT(novo());
    else if (v !== SEM) setT(timbrados.find((x) => x.id === v) ?? novo());
  };
  const aplicar = () => {
    if (escolha === SEM) return aoAplicar(null);
    const original = timbrados.find((x) => x.id === t.id);
    const mudou = !original || JSON.stringify(original) !== JSON.stringify(t);
    aoAplicar(t.id, mudou && !t.somenteLeitura ? { ...t, rotulo: rotuloDoTimbrado(t) } : undefined);
  };

  return (
    <PainelDialogo
      aberto={aberto}
      aoFechar={aoFechar}
      titulo="Papel timbrado"
      descricao="Cadastrado uma vez pelo escritório; escolhido em cada peça."
      rodape={rodapeModal(aplicar, aoFechar, 'Usar este timbrado')}
    >
      <div onKeyDown={enterAplica(aplicar)}>
        <EditorDeTimbrado
          t={escolha === SEM ? null : t}
          aoMudar={setT}
          modos={modos}
          antes={
            <section className="cm-ef-secao">
              <h3>Timbrado desta peça</h3>
              <select className="cm-ef-campo" value={escolha} onChange={(e) => mudarEscolha(e.target.value)}>
                {timbrados.map((x) => <option key={x.id} value={x.id}>{x.rotulo}</option>)}
                <option value={SEM}>Sem timbrado</option>
                <option value={NOVO}>Cadastrar um novo…</option>
              </select>
            </section>
          }
        />
      </div>
    </PainelDialogo>
  );
}

/* ===================== Laterais ===================== */

export function Lateral({ titulo, subtitulo, aoFechar, children, rodape }: { titulo: string; subtitulo?: string; aoFechar: () => void; children: ReactNode; rodape?: ReactNode }) {
  return (
    <aside className="cm-ef-lateral" aria-label={titulo}>
      <div className="cm-ef-lat-cab">
        <div><h2>{titulo}</h2>{subtitulo && <p>{subtitulo}</p>}</div>
        <BotaoNu className="cm-ef-bt" aria-label="Fechar" onClick={aoFechar}>
          <svg viewBox="0 0 24 24" className="cm-ef-ico" aria-hidden><path d="M6 6l12 12M18 6 6 18" /></svg>
        </BotaoNu>
      </div>
      <div className="cm-ef-lat-corpo">{children}</div>
      {rodape && <div className="cm-ef-lat-rod">{rodape}</div>}
    </aside>
  );
}

export function ConteudoBusca({ termo, novo, caixa, inteira, total, atual, aoMudar, aoAnterior, aoProximo, aoSubstituir, aoSubstituirTodos, focarSubstituir }: {
  termo: string; novo: string; caixa: boolean; inteira: boolean; total: number; atual: number;
  aoMudar: (p: { termo?: string; novo?: string; caixa?: boolean; inteira?: boolean }) => void;
  aoAnterior: () => void; aoProximo: () => void; aoSubstituir: () => void; aoSubstituirTodos: () => void; focarSubstituir: boolean;
}) {
  return (
    <>
      <label className="cm-ef-rot">Localizar
        <input className="cm-ef-campo" value={termo} autoFocus={!focarSubstituir || !termo} onChange={(e) => aoMudar({ termo: e.target.value })}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (e.shiftKey) aoAnterior(); else aoProximo(); } }} />
      </label>
      <label className="cm-ef-rot">Substituir por
        <input className="cm-ef-campo" value={novo} autoFocus={focarSubstituir && !!termo} onChange={(e) => aoMudar({ novo: e.target.value })} />
      </label>
      <label className="cm-ef-check"><input type="checkbox" checked={caixa} onChange={(e) => aoMudar({ caixa: e.target.checked })} /> Diferenciar maiúsculas e minúsculas</label>
      <label className="cm-ef-check"><input type="checkbox" checked={inteira} onChange={(e) => aoMudar({ inteira: e.target.checked })} /> Só palavras inteiras</label>
      <span className="cm-ef-contagem" aria-live="polite">{!termo ? '' : total ? `${atual + 1} de ${total} ocorrências` : 'Nenhuma ocorrência'}</span>
      <div className="cm-ef-linha-bt">
        <Button type="button" size="sm" variant="outline" disabled={!total} onClick={aoAnterior}>Anterior</Button>
        <Button type="button" size="sm" variant="outline" disabled={!total} onClick={aoProximo}>Próximo</Button>
        <Button type="button" size="sm" variant="outline" disabled={!total} onClick={aoSubstituir}>Substituir</Button>
        <Button type="button" size="sm" disabled={!total} onClick={aoSubstituirTodos}>Substituir tudo</Button>
      </div>
    </>
  );
}
